import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import SectionHeader from './SectionHeader';
import TIMELINE_EVENTS from '../data/timeline';
import VehicleIcon from './VehicleIcons';
import ClampText from './ClampText';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

function resolveImagePath(imagePath) {
  if (typeof imagePath !== 'string' || imagePath.length === 0) {
    return '';
  }

  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  const normalizedPath = imagePath.replace(/^\/+/, '');
  return `${process.env.PUBLIC_URL}/${normalizedPath}`;
}

const MOBILE_BREAK = 760;

// Life doesn't run in a straight line — neither does this road. The SVG path
// weaves left and right through the timeline, filling in as you scroll.
function JourneySection({ language, mode }) {
  const sectionRef = useRef(null);
  const flowRef = useRef(null);
  const roadBaseRef = useRef(null);
  const roadInkRef = useRef(null);
  const itemRefs = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  const isTraveler = mode === 'traveler';

  const events = useMemo(() => {
    const list = isTraveler
      ? [...TIMELINE_EVENTS]
      : TIMELINE_EVENTS.filter((event) => event.kind !== 'travel');
    return list.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [isTraveler]);

  const laneFor = useCallback(
    (event, index) => {
      if (!isTraveler) {
        return index % 2 === 0 ? 'left' : 'right';
      }
      if (event.kind === 'travel') {
        return 'right';
      }
      if (event.kind === 'both') {
        return 'center';
      }
      return 'left';
    },
    [isTraveler]
  );

  const [revealed, setRevealed] = useState(() => new Set());
  const [lineProgress, setLineProgress] = useState(0);
  const [road, setRoad] = useState({ d: '', width: 0, height: 0, dots: [] });

  itemRefs.current = [];

  // --- measure card anchors and build the winding road path ---------------
  const measureRoad = useCallback(() => {
    const container = flowRef.current;
    if (!container) {
      return;
    }

    const W = container.clientWidth;
    const H = container.clientHeight;
    if (!W || !H) {
      return;
    }

    // Same predicate as the CSS card-lane breakpoint (viewport, not container),
    // otherwise the road switches to mobile geometry while the cards are still
    // in two lanes around ~761-844px viewports.
    const isMobile =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(`(max-width: ${MOBILE_BREAK}px)`).matches
        : W < MOBILE_BREAK;
    const anchors = [];

    itemRefs.current.forEach((el, index) => {
      if (!el) {
        return;
      }
      const lane = el.getAttribute('data-lane');
      const y = el.offsetTop + 30;
      let x;
      if (isMobile) {
        x = 16 + (index % 2 === 0 ? -7 : 7);
      } else if (lane === 'left') {
        x = W * 0.585;
      } else if (lane === 'right') {
        x = W * 0.415;
      } else {
        x = W * 0.5 + (index % 2 === 0 ? -14 : 14);
      }
      anchors.push({ x, y });
    });

    if (anchors.length === 0) {
      return;
    }

    const startX = isMobile ? 16 : W * 0.5;
    let d = `M${startX.toFixed(1)} 0`;
    let prev = { x: startX, y: 0 };
    anchors.forEach((a) => {
      const ym = (prev.y + a.y) / 2;
      d += ` C${prev.x.toFixed(1)} ${ym.toFixed(1)}, ${a.x.toFixed(1)} ${ym.toFixed(1)}, ${a.x.toFixed(1)} ${a.y.toFixed(1)}`;
      prev = a;
    });
    const endY = H;
    const endX = isMobile ? 16 : W * 0.5;
    const ym = (prev.y + endY) / 2;
    d += ` C${prev.x.toFixed(1)} ${ym.toFixed(1)}, ${endX.toFixed(1)} ${ym.toFixed(1)}, ${endX.toFixed(1)} ${endY.toFixed(1)}`;

    setRoad({ d, width: W, height: H, dots: anchors });
  }, []);

  useLayoutEffect(() => {
    measureRoad();

    if (typeof window === 'undefined') {
      return undefined;
    }

    let observer;
    if ('ResizeObserver' in window && flowRef.current) {
      observer = new ResizeObserver(() => {
        measureRoad();
      });
      observer.observe(flowRef.current);
    }
    window.addEventListener('resize', measureRoad);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', measureRoad);
    };
  }, [measureRoad, events]);

  // --- ink fill driven by scroll ------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined' || prefersReducedMotion) {
      setLineProgress(1);
      return undefined;
    }

    let frameId = null;

    const updateProgress = () => {
      frameId = null;
      const element = sectionRef.current;
      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const entered = viewportHeight * 0.82 - rect.top;
      const totalDistance = rect.height + viewportHeight * 0.36;
      const normalized = Math.min(1, Math.max(0, entered / totalDistance));

      setLineProgress(normalized);
    };

    const requestProgressUpdate = () => {
      if (frameId === null) {
        frameId = window.requestAnimationFrame(updateProgress);
      }
    };

    requestProgressUpdate();
    window.addEventListener('scroll', requestProgressUpdate, { passive: true });
    window.addEventListener('resize', requestProgressUpdate);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener('scroll', requestProgressUpdate);
      window.removeEventListener('resize', requestProgressUpdate);
    };
  }, [prefersReducedMotion]);

  // Apply the fill imperatively (cheap: two style writes per frame).
  useEffect(() => {
    const ink = roadInkRef.current;
    if (!ink || !road.d || typeof ink.getTotalLength !== 'function') {
      return;
    }
    const length = ink.getTotalLength();
    ink.style.strokeDasharray = `${length}`;
    ink.style.strokeDashoffset = `${length * (1 - lineProgress)}`;
  }, [lineProgress, road.d]);

  // --- reveal cards ---------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined' || prefersReducedMotion || !('IntersectionObserver' in window)) {
      setRevealed(new Set(events.map((event) => event.id)));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }
          const id = entry.target.getAttribute('data-event-id');
          setRevealed((prev) => {
            if (prev.has(id)) {
              return prev;
            }
            const next = new Set(prev);
            next.add(id);
            return next;
          });
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -6% 0px' }
    );

    itemRefs.current.forEach((item) => {
      if (item) {
        observer.observe(item);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [events, prefersReducedMotion]);

  const copy =
    language === 'ja'
      ? {
          label: 'JOURNEY',
          title: 'これまでの歩み',
          lead: isTraveler
            ? '開発者としての道は左へ、旅人としての道は右へ。人生はまっすぐには進みません。'
            : '奈良の実家から、東京、バンクーバー、そしてフリーランスへ。道はまっすぐではありませんでした。',
          travelLabel: '旅の記録',
          moreLabel: '旅のプロジェクトで詳しく',
          ariaLabel: '経歴タイムライン',
          laneDev: 'ENGINEER',
          laneTravel: 'TRAVELER',
        }
      : {
          label: 'JOURNEY',
          title: 'Journey so far',
          lead: isTraveler
            ? 'The engineer’s road runs left, the traveler’s right. Life does not move in a straight line.'
            : 'From Nara to Tokyo, Vancouver, and into freelance work — never in a straight line.',
          travelLabel: 'Travel log',
          moreLabel: 'More in the travel projects',
          ariaLabel: 'Career timeline',
          laneDev: 'ENGINEER',
          laneTravel: 'TRAVELER',
        };

  return (
    <section id="journey" className="journey-section" ref={sectionRef} aria-label={copy.ariaLabel}>
      <SectionHeader index="03" label={copy.label} title={copy.title} lead={copy.lead} />

      {isTraveler ? (
        <div className="journey-lanes mono" aria-hidden="true">
          <span className="journey-lanes__dev">← {copy.laneDev}</span>
          <span className="journey-lanes__travel">{copy.laneTravel} →</span>
        </div>
      ) : null}

      <div className="journey-flow" ref={flowRef}>
        {road.d ? (
          <svg
            className="journey-road"
            viewBox={`0 0 ${road.width} ${road.height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ width: road.width, height: road.height }}
          >
            <path ref={roadBaseRef} className="journey-road__base" d={road.d} />
            <path ref={roadInkRef} className="journey-road__ink" d={road.d} />
            {road.dots.map((dot, i) => (
              <circle
                key={`dot-${i}`}
                className={`journey-road__dot ${
                  lineProgress >= dot.y / Math.max(1, road.height) ? 'is-passed' : ''
                }`}
                cx={dot.x}
                cy={dot.y}
                r="5"
              />
            ))}
          </svg>
        ) : null}

        <ol className="journey-list">
          {events.map((event, index) => {
            const lane = laneFor(event, index);
            const isVisible = revealed.has(event.id);
            const imageSource = resolveImagePath(event.image);
            const isTravelCard = event.kind === 'travel';
            const travels = !isTraveler ? event.travels || [] : [];
            const notes = event.notes[language].map((note, noteIndex) => (
              <p key={`${event.id}-note-${noteIndex}`} className="journey-note">
                {note}
              </p>
            ));

            return (
              <li
                key={event.id}
                data-event-id={event.id}
                data-lane={lane}
                className={`journey-node is-${lane} ${isTravelCard ? 'is-travel' : ''} ${
                  isVisible ? 'is-visible' : ''
                }`}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
              >
                {isTravelCard ? (
                  // Journeys are lighter than life events: a slim card with
                  // just a thumbnail, the title, and a link to the project.
                  <article className="journey-card journey-card--travel journey-card--mini">
                    {imageSource ? (
                      <img className="journey-card__thumb" src={imageSource} alt="" loading="lazy" />
                    ) : (
                      <span className="journey-card__thumb journey-card__thumb--glyph" aria-hidden="true">
                        <VehicleIcon type={event.vehicle} strokeWidth={1.4} />
                      </span>
                    )}
                    <div className="journey-card__mini-body">
                      <p className="journey-card__period mono">
                        <VehicleIcon type={event.vehicle} className="journey-card__vehicle" strokeWidth={1.8} />
                        {event.period[language]}
                      </p>
                      <h3 className="journey-card__title journey-card__title--mini">
                        {event.title[language]}
                      </h3>
                      {event.tripRef ? (
                        <a className="journey-card__more mono" href={`#travel-${event.tripRef}`}>
                          {copy.moreLabel} →
                        </a>
                      ) : null}
                    </div>
                  </article>
                ) : (
                <article className="journey-card">
                  <p className="journey-card__period mono">{event.period[language]}</p>
                  <h3 className="journey-card__title">{event.title[language]}</h3>

                  <div
                    className={`journey-card__detail ${
                      imageSource ? '' : 'journey-card__detail--no-image'
                    }`}
                  >
                    {imageSource ? (
                      <figure className="journey-card__visual">
                        {/* Decorative: the adjacent heading already names the event. */}
                        <img src={imageSource} alt="" loading="lazy" />
                      </figure>
                    ) : null}

                    <div className={`journey-bubble ${imageSource ? 'journey-bubble--side' : ''}`}>
                      <span className="journey-bubble__tail" aria-hidden="true" />
                      {event.collapseNotes ? (
                        // On phones, long stories open after their first paragraph.
                        <ClampText language={language} paragraphs={1} className="clamp-text--paragraphs">
                          {notes}
                        </ClampText>
                      ) : (
                        notes
                      )}

                      {travels.length > 0 ? (
                        <ul className="journey-travels" aria-label={copy.travelLabel}>
                          {travels.map((travel, travelIndex) => (
                            <li key={`${event.id}-travel-${travelIndex}`} className="journey-travel mono">
                              <span className="journey-travel__glyph" aria-hidden="true">
                                —
                              </span>
                              {travel[language]}
                            </li>
                          ))}
                        </ul>
                      ) : null}

                      {event.link ? (
                        <a className="journey-card__more journey-card__link mono" href={event.link.href}>
                          {event.link.label[language]} →
                        </a>
                      ) : null}

                      {travels.length > 0 && event.travelsClose ? (
                        <p className="journey-note journey-note--travels-close">
                          {event.travelsClose[language]}
                        </p>
                      ) : null}
                    </div>
                  </div>

                </article>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

export default JourneySection;
